import React, { useEffect, useState } from "react";
import { Activity as ActivityIcon, Loader2 } from "lucide-react";
import { api, apiError } from "../lib/api";
import { ENTITY_GROUPS } from "../lib/activity";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import ActivityList from "../components/ActivityList";
import EmptyState from "../components/EmptyState";
import ListSkeleton from "../components/ListSkeleton";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";

const PAGE = 30;
const EMPTY = { user_id: "", site_id: "", group: "", date_from: "", date_to: "" };
const selectClass = "min-h-11 w-full rounded-md border border-border bg-background px-3";

function params(filters, before) {
  const group = ENTITY_GROUPS.find(([key]) => key === filters.group);
  const query = new URLSearchParams({ limit: String(PAGE) });
  ["user_id", "site_id", "date_from", "date_to"].forEach((key) => { if (filters[key]) query.set(key, filters[key]); });
  (group?.[2] || []).forEach((entity) => query.append("entity", entity));
  if (before) query.set("before", before);
  return query;
}

/** Everything recorded that this person may see, newest first, with filters. */
export default function Activity() {
  const { user } = useAuth();
  const [filters, setFilters] = useState(EMPTY);
  const [rows, setRows] = useState(null);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [people, setPeople] = useState([]);
  // The site filter helps only when several sites are shown together.
  const sites = user?.active_site_id === "all" ? user?.sites || [] : [];

  useEffect(() => {
    let live = true;
    api.get("/activity/people").then(({ data }) => { if (live) setPeople(data); }).catch(() => {});
    return () => { live = false; };
  }, [user?.active_site_id]);

  useEffect(() => {
    let live = true;
    setRows(null); setError("");
    api.get(`/activity?${params(filters)}`)
      .then(({ data }) => { if (live) { setRows(data); setMore(data.length === PAGE); } })
      .catch((e) => { if (live) setError(apiError(e)); });
    return () => { live = false; };
  }, [filters, user?.active_site_id]);

  const loadMore = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/activity?${params(filters, rows[rows.length - 1].timestamp)}`);
      setRows((current) => [...current, ...data]);
      setMore(data.length === PAGE);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  const set = (key) => (event) => setFilters((current) => ({ ...current, [key]: event.target.value }));
  const filtered = Object.values(filters).some(Boolean);
  // Operators and drivers see only their own activity: no one else to choose.
  const ownOnly = people.every((person) => person.id === user?.id);

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader icon={ActivityIcon} trail={[{ label: "Dashboard", to: "/" }, { label: "Attività" }]} title={ownOnly ? "Le tue attività" : "Attività"}
        description={ownOnly ? "Cosa hai fatto, su quale veicolo, cliente o noleggio." : "Chi ha fatto cosa, su quale veicolo, cliente o noleggio. Si vedono solo le attività delle sezioni a cui hai accesso."} />
      <Card className="grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="activity-filters">
        {!ownOnly && <label className="space-y-1.5 text-sm"><span>Persona</span>
          <select className={selectClass} value={filters.user_id} onChange={set("user_id")} data-testid="activity-filter-user">
            <option value="">Tutte</option>
            {people.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
          </select></label>}
        {sites.length > 1 && <label className="space-y-1.5 text-sm"><span>Sede</span>
          <select className={selectClass} value={filters.site_id} onChange={set("site_id")} data-testid="activity-filter-site">
            <option value="">Tutte</option>
            {sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
          </select></label>}
        <label className="space-y-1.5 text-sm"><span>Tipo</span>
          <select className={selectClass} value={filters.group} onChange={set("group")} data-testid="activity-filter-group">
            <option value="">Tutti</option>
            {ENTITY_GROUPS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select></label>
        <label className="space-y-1.5 text-sm"><span>Dal</span>
          <Input type="date" className="min-h-11" value={filters.date_from} max={filters.date_to || undefined} onChange={set("date_from")} data-testid="activity-filter-from" /></label>
        <label className="space-y-1.5 text-sm"><span>Al</span>
          <Input type="date" className="min-h-11" value={filters.date_to} min={filters.date_from || undefined} onChange={set("date_to")} data-testid="activity-filter-to" /></label>
        {filtered && <div className="sm:col-span-2 lg:col-span-4"><Button variant="outline" size="sm" onClick={() => setFilters(EMPTY)}>Azzera filtri</Button></div>}
      </Card>
      {error && <Card role="alert" className="p-4 text-sm">Attività non disponibili. {error}</Card>}
      {!rows && !error && <ListSkeleton rows={6} label="Caricamento attività…" />}
      {rows && (
        <Card className="p-3 sm:p-4">
          {rows.length === 0
            ? <EmptyState icon={ActivityIcon} title="Nessuna attività" description={filtered ? "Nessuna attività corrisponde ai filtri." : "Le attività compaiono qui man mano che si lavora."} />
            : <ActivityList rows={rows} />}
          {more && <div className="mt-3 flex justify-center">
            <Button variant="outline" disabled={busy} onClick={loadMore} data-testid="activity-more">{busy && <Loader2 className="animate-spin" aria-hidden="true" />}Carica altre</Button>
          </div>}
        </Card>
      )}
    </div>
  );
}
